/**
 * Dependency-free router for the application routes.
 *
 * Only path matching, `history.pushState` and a change subscription are needed
 * today; a router library would add weight for no feature. Routes are declared
 * once here so links and the shell cannot disagree on a path.
 */
export const ROUTES = {
  game: "/",
  wallet: "/wallet",
  adminAudit: "/admin/audit",
  adminSettings: "/admin/settings",
  adminThemes: "/admin/themes",
  adminVehicles: "/admin/vehicles",
  provablyFair: "/provably-fair",
  howToPlay: "/how-to-play",
  verifyEmail: "/auth/verify-email",
  resetPassword: "/auth/reset-password",
} as const;

export type RouteId = keyof typeof ROUTES;
export type RoutePath = (typeof ROUTES)[RouteId];

const ROUTE_IDS = Object.keys(ROUTES) as RouteId[];

/** `/provably-fair/` and `/provably-fair` are the same route. */
function normalize(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

export function matchRoute(pathname: string): RouteId | null {
  const path = normalize(pathname);
  return ROUTE_IDS.find((id) => ROUTES[id] === path) ?? null;
}

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener();
}

export function currentPath(): string {
  return normalize(window.location.pathname);
}

export function navigate(path: RoutePath): void {
  if (currentPath() === path) return;
  window.history.pushState(null, "", path);
  notify();
}

/** Subscribe to navigation, including the browser's back and forward buttons. */
export function subscribeToPath(listener: Listener): () => void {
  if (listeners.size === 0) window.addEventListener("popstate", notify);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener("popstate", notify);
  };
}
